import React, { useEffect, useState } from "react";
import { RotateCcw, Save, Loader2, Eraser } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

const label = (p) => p.replace(/^\^?\/api\//, "/api/").replace(/\$$/, "").replace(/\\/g, "");

export function RateLimits() {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/rate-limits").then((r) => { setData(r.data); setEdits({}); })
    .catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const upd = (key, field, v) => setEdits((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: v } }));

  const save = async (r) => {
    const e = edits[r.key] || {};
    const limit = Number(e.limit ?? r.limit);
    const window_sec = Number(e.window_sec ?? r.window_sec);
    if (!limit || !window_sec) return toast.error("Limit and window must be greater than 0");
    setBusy(r.key);
    try { await api.patch("/rate-limits", { key: r.key, limit, window_sec }); toast.success("Rate limit updated"); await load(); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(null); }
  };

  const reset = async (r) => {
    setBusy(r.key);
    try { await api.post("/rate-limits/reset", { key: r.key }); toast.success("Reset to default"); await load(); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(null); }
  };

  const clearCounters = async () => {
    if (!window.confirm("Clear all active rate-limit counters? Anyone currently blocked will be unblocked.")) return;
    setBusy("clear");
    try { const { data: d } = await api.post("/rate-limits/clear-counters", { key_prefix: "" }); toast.success(`Cleared ${d.deleted} counters`); await load(); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(null); }
  };

  if (!data) return <div className="mono-label" data-testid="rate-limits-loading">Loading rate limits…</div>;

  return (
    <Panel className="p-6" >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonoLabel>Rate limits · {data.active_keys} active counters</MonoLabel>
        <button data-testid="rl-clear-counters" className="btn-ghost" disabled={busy === "clear"} onClick={clearCounters}>
          {busy === "clear" ? <Loader2 size={14} className="animate-spin" /> : <Eraser size={14} />} Clear active counters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left mono-label">
              <th className="py-3 pr-3">Endpoint</th>
              <th className="py-3 pr-3">Scope</th>
              <th className="py-3 pr-3">Requests</th>
              <th className="py-3 pr-3">Per (seconds)</th>
              <th className="py-3 pr-3">Default</th>
              <th className="py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.rules.map((r, i) => {
              const e = edits[r.key] || {};
              const dirty = (e.limit != null && Number(e.limit) !== r.limit) || (e.window_sec != null && Number(e.window_sec) !== r.window_sec);
              return (
                <tr key={r.key} className="hairline-t" data-testid={`rl-row-${i}`}>
                  <td className="py-2 pr-3">
                    <div className="mono-label" style={{ color: "var(--text)" }}>{r.method} {label(r.pattern)}</div>
                  </td>
                  <td className="py-2 pr-3 mono-label" style={{ color: "var(--taupe)" }}>{r.scope}</td>
                  <td className="py-2 pr-3">
                    <input data-testid={`rl-limit-${i}`} type="number" min="1" className="input-x !py-2 !w-28"
                      value={e.limit ?? r.limit} onChange={(ev) => upd(r.key, "limit", ev.target.value)} />
                  </td>
                  <td className="py-2 pr-3">
                    <input data-testid={`rl-window-${i}`} type="number" min="1" className="input-x !py-2 !w-32"
                      value={e.window_sec ?? r.window_sec} onChange={(ev) => upd(r.key, "window_sec", ev.target.value)} />
                  </td>
                  <td className="py-2 pr-3 mono-label" style={{ color: r.overridden ? "var(--red-2)" : "var(--muted)" }}>
                    {r.default_limit}/{r.default_window_sec}s{r.overridden ? " · overridden" : ""}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button data-testid={`rl-save-${i}`} className="btn-red !py-1.5 !px-3" disabled={!dirty || busy === r.key} onClick={() => save(r)}>
                      {busy === r.key ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                    </button>
                    {r.overridden && (
                      <button data-testid={`rl-reset-${i}`} className="p-2 ml-1 hover:text-[var(--red)]" title="Reset to default" onClick={() => reset(r)}>
                        <RotateCcw size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mono-label mt-5" style={{ color: "var(--muted)" }}>
        Changes apply within ~10 seconds across all backend instances. "Clear active counters" wipes current
        per-IP / per-phone / per-device counters so blocked users can retry immediately.
      </div>
    </Panel>
  );
}
